import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  DestroyRef,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  effect,
  forwardRef,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgControl, ReactiveFormsModule } from '@angular/forms';
import { IconComponent } from '@components';
import { BehaviorSubject, combineLatest } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  skip,
  tap,
} from 'rxjs/operators';
import { BaseFormControlComponent } from '../../../shared/components/base-form-control/base-form-control.component';
import { FormLoadingSpinnerComponent } from '../../../shared/components/form-loading-spinner/form-loading-spinner.component';
import { ValidityIconComponent } from '../../../shared/components/validity-icon/validity-icon.component';
import { FormControlDirective } from '../../../shared/directives/form-control/form-control.directive';
import { FormOption } from '../../../shared/models/form-option.interface';
import { controlValue$ } from '../../../shared/utils/form-control/form-control.utils';
import { SelectDropdownComponent } from '../select-dropdown/select-dropdown.component';

@Component({
  selector: 'app-form-select-typeahead',
  templateUrl: './select-typeahead.component.html',
  styleUrls: ['./select-typeahead.component.scss'],
  providers: [
    {
      provide: BaseFormControlComponent,
      useExisting: forwardRef(() => SelectTypeaheadComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormControlDirective,
    FormLoadingSpinnerComponent,
    IconComponent,
    NgClass,
    NgTemplateOutlet,
    ReactiveFormsModule,
    SelectDropdownComponent,
    ValidityIconComponent,
  ],
  standalone: true,
})
export class SelectTypeaheadComponent
  extends BaseFormControlComponent<string>
  implements OnInit
{
  protected override readonly changeDetectorRef = inject(ChangeDetectorRef);
  protected override readonly ngControl = inject(NgControl);
  protected override readonly destroyRef = inject(DestroyRef);

  constructor() {
    super();

    effect(() => {
      const inputOptions = this.options().value;
      const searchTerm = this.searchTerm$.value.trim();
      let options = inputOptions ?? [];

      if (this.unmatchedSelection() && !options.length && !!searchTerm) {
        options = [{ label: searchTerm, value: searchTerm }];
      }

      this.options$.next(options);
      this.searching$.next(false);
    });
  }

  readonly options = input<
    { value: FormOption[] | null | undefined },
    FormOption[] | null | undefined
  >(
    { value: undefined },
    {
      transform: (value: FormOption[] | null | undefined) => ({ value }),
    },
  );
  readonly setValueOnSelection = input<boolean | undefined>(true);
  readonly unmatchedSelection = input<boolean>();
  readonly noResultsLabel = input<string | undefined>('No results found');
  readonly placeholder = input<string | undefined>('Start typing');
  readonly optionSubLabels = input<string[] | undefined>([]);

  @Output() optionSelected = new EventEmitter<FormOption>();
  @Output() searched = new EventEmitter<string>();

  @ViewChild('searchInputElement', { static: false })
  searchInputElement?: ElementRef;
  @ContentChild('optionTemplate', { static: false })
  optionTemplate?: TemplateRef<unknown>;

  // Observables
  expanded$ = new BehaviorSubject<boolean>(false);
  options$ = new BehaviorSubject<FormOption[]>([]);
  searching$ = new BehaviorSubject<boolean>(false);
  searchInputFocusedSubject = new BehaviorSubject<boolean>(false);
  searchTerm$ = new BehaviorSubject<string>('');
  selectedOption$ = new BehaviorSubject<FormOption | undefined>(undefined);

  // Local values for template binding
  expanded = false;
  optionsList: FormOption[] = [];
  searching = false;
  searchInputFocused = false;
  searchTerm = '';
  selectedOption?: FormOption;
  valueString: string | undefined;

  searchElementId!: string;

  override ngOnInit(): void {
    super.ngOnInit();

    this.searchElementId = this.id + '__search';

    // Subscribe observables → locals
    this.expanded$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => {
        this.expanded = val;
        this.changeDetectorRef.markForCheck();
      });

    this.options$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((opts) => {
        this.optionsList = opts;
        this.changeDetectorRef.markForCheck();
      });

    this.searching$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => {
        this.searching = val;
        this.changeDetectorRef.markForCheck();
      });

    this.searchInputFocusedSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => {
        this.searchInputFocused = val;
        this.changeDetectorRef.markForCheck();
      });

    this.searchTerm$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => {
        this.searchTerm = val;
        this.changeDetectorRef.markForCheck();
      });

    this.selectedOption$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((opt) => {
        this.selectedOption = opt;
        this.changeDetectorRef.markForCheck();
      });

    this.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => {
        this.valueString = val;
        this.changeDetectorRef.markForCheck();
      });

    this.collapseOnSelection();
    this.populateSelectedOption();
    this.listenToSearchControlChanges();
    this.listenToSearchingChanges();
    this.listenToEnabledChanges();
    this.listenToControlReset();
  }

  expand = (): void => {
    if (this.enabled) {
      this.expanded$.next(true);
    }
  };

  collapse = (): void => {
    this.expanded$.next(false);
  };

  onSelection = (option: FormOption): void => {
    this.optionSelected.emit(option);
    this.searchInputFocusedSubject.next(false);

    if (this.setValueOnSelection()) {
      this.setValue(option.value);
    }
  };

  clearInputValue = (): void => {
    this.clear();
    this.searchTerm$.next('');
    this.searched.emit('');
    this.options$.next([]);
  };

  onSearchInputBlur = (): void => {
    if (this.expanded) {
      return;
    }
    this.onBlur();
    this.searchInputFocusedSubject.next(false);
  };

  onDisplayInputFocus(): void {
    this.onFocus();
    this.searchInputFocusedSubject.next(true);
    this.changeDetectorRef.detectChanges();

    if (this.searchInputElement) {
      this.searchInputElement.nativeElement.focus();
    }
  }

  searchInputElementChange = (): void => {
    this.searchTerm$.next(this.searchInputElement?.nativeElement.value ?? '');
  };

  private collapseOnSelection(): void {
    this.valueChanges
      .pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.collapse();
      });
  }

  private populateSelectedOption(): void {
    combineLatest([this.options$, controlValue$<string>(this.control)])
      .pipe(
        map(([options, value]: [FormOption[], string | undefined]) =>
          options.find((option: FormOption) => option.value === value),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((selectedOption?: FormOption) => {
        this.selectedOption$.next(selectedOption);
      });
  }

  private listenToSearchControlChanges(): void {
    this.searchTerm$
      .pipe(
        skip(1),
        filter(() => this.enabled),
        tap(() => this.searching$.next(true)),
        debounceTime(300),
        map((searchTerm: string) => searchTerm.trim()),
        distinctUntilChanged((previous: string, current: string) =>
          this.isUnchangedSearchTerm(previous, current),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((searchTerm: string) => {
        this.searched.emit(searchTerm);
      });
  }

  private isUnchangedSearchTerm(previous: string, current: string): boolean {
    const unchanged = previous === current;
    if (unchanged) {
      this.searching$.next(false);
    }

    return unchanged;
  }

  private listenToSearchingChanges(): void {
    combineLatest([this.searching$, this.searchInputFocusedSubject])
      .pipe(
        distinctUntilChanged(),
        filter(
          ([searching, searchInputFocused]: boolean[]) =>
            !searching && searchInputFocused && !this.expanded,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.expand());
  }

  private listenToEnabledChanges(): void {
    this.enabledChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.changeDetectorRef.detectChanges();
      });
  }

  private listenToControlReset(): void {
    this.valueChanges
      .pipe(
        map((value: string | undefined) => !value),
        distinctUntilChanged(),
        filter(Boolean),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.clearInputValue();
      });
  }
}
